import React, { useEffect, useRef, useState } from "react";
import { Anchor, Lock } from "lucide-react";

const STORAGE_KEY = "rrdctips_unlocked";
const PASSWORD = "Camp1993!";

export default function PasswordGate({ children }) {
  const [unlocked, setUnlocked] = useState(() => {
    try {
      return typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [logoOk, setLogoOk] = useState(true);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!unlocked && inputRef.current) inputRef.current.focus();
  }, [unlocked]);

  const submit = (e) => {
    e.preventDefault();
    if (value === PASSWORD) {
      try {
        window.localStorage.setItem(STORAGE_KEY, "true");
      } catch {
        /* ignore storage errors */
      }
      setError("");
      setUnlocked(true);
    } else {
      setError("Incorrect password");
      setValue("");
      if (inputRef.current) inputRef.current.focus();
    }
  };

  if (unlocked) return children;

  return (
    <div
      data-testid="password-gate"
      className="min-h-screen w-full flex items-center justify-center px-4 py-8"
      style={{
        background:
          "radial-gradient(circle at top, #EEF3FB 0%, #F1E9D6 55%, #F1E9D6 100%)",
      }}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-[#E9DDC8] p-8 sm:p-10 flex flex-col items-center"
      >
        <div
          className="w-24 h-24 sm:w-28 sm:h-28 rounded-full flex items-center justify-center mb-5 overflow-hidden"
          style={{ backgroundColor: logoOk ? "transparent" : "#2F50A1" }}
        >
          {logoOk ? (
            <img
              src="/rrdc-logo.png"
              alt="Rolling River Day Camp"
              className="w-full h-full object-contain"
              onError={() => setLogoOk(false)}
            />
          ) : (
            <Anchor className="w-10 h-10 text-white" strokeWidth={2.2} />
          )}
        </div>

        <div
          className="font-mono uppercase tracking-widest text-[11px]"
          style={{ color: "#2F50A1", letterSpacing: "0.2em" }}
        >
          Rolling River Day Camp
        </div>
        <h1
          className="font-display text-3xl sm:text-4xl font-bold text-center mt-1"
          style={{ color: "#0B2545" }}
        >
          Tip Letters
        </h1>
        <div className="w-16 h-1 rounded-full mt-3" style={{ backgroundColor: "#E6C425" }} />

        <p className="mt-5 text-center text-sm text-[#13315C]/75">
          Enter the camp password to view this year&apos;s tip letters.
        </p>

        <label htmlFor="rr-pw" className="sr-only">Password</label>
        <div className="relative w-full mt-6">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#13315C]/40" />
          <input
            id="rr-pw"
            ref={inputRef}
            type="password"
            autoComplete="current-password"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError("");
            }}
            placeholder="Password"
            data-testid="password-gate-input"
            className="w-full pl-10 pr-3 py-3 rounded-xl border-2 bg-white text-[#0B2545] placeholder:text-[#13315C]/40 focus:outline-none transition-colors"
            style={{
              borderColor: error ? "#B54223" : "#E9DDC8",
            }}
            onFocus={(e) => {
              if (!error) e.target.style.borderColor = "#2F50A1";
            }}
            onBlur={(e) => {
              if (!error) e.target.style.borderColor = "#E9DDC8";
            }}
          />
        </div>

        {error && (
          <div
            data-testid="password-gate-error"
            className="w-full mt-3 text-sm text-[#B54223] flex items-center gap-1.5"
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: "#B54223" }}
            />
            {error}
          </div>
        )}

        <button
          type="submit"
          data-testid="password-gate-submit"
          className="w-full mt-5 py-3 rounded-xl font-semibold text-white transition-transform active:scale-[0.99] hover:brightness-110"
          style={{ backgroundColor: "#2F50A1" }}
        >
          Enter
        </button>

        <p className="mt-6 text-[11px] text-[#13315C]/50 text-center">
          For camp staff &amp; families only.
        </p>
      </form>
    </div>
  );
}
